import { PercentagePoint } from "types/testTypes";
import h337, { Heatmap } from "heatmap.js";
import { useEffect, useRef, useState } from "react";

import { Box } from "@mantine/core";

interface HeatmapProps {
  percentages: PercentagePoint[] | null;
  isRealtime: boolean;
  percentShown?: number;
}

const POINT_INTENSITY = 1;

const HeatMap = ({ percentages, isRealtime, percentShown = 100 }: HeatmapProps) => {
  const heatmapRef = useRef<HTMLDivElement>(null);
  const heatmapInstance = useRef<Heatmap<any, any, any> | null>(null);
  const rendered = useRef({ count: 0, width: 0, height: 0 });
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Keep dimensions in sync with the container so gaze points always map to the
  // current canvas size (the tile width changes with the responsive grid).
  useEffect(() => {
    const container = heatmapRef.current;
    if (!container) {
      return;
    }
    const measure = () => {
      setDimensions((prev) =>
        prev.width === container.offsetWidth && prev.height === container.offsetHeight
          ? prev
          : { width: container.offsetWidth, height: container.offsetHeight },
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // heatmap.js fixes its canvas size at create() and never resizes it on its
  // own, so the instance is recreated whenever the container size changes.
  useEffect(() => {
    const container = heatmapRef.current;
    if (!container || !dimensions.width || !dimensions.height) {
      return;
    }
    container.querySelectorAll("canvas").forEach((canvas) => canvas.remove());
    heatmapInstance.current = h337.create({
      container,
      radius: 30,
      maxOpacity: 0.6,
      minOpacity: 0.2,
      blur: 0.85,
    });
    return () => {
      heatmapInstance.current = null;
    };
  }, [dimensions.width, dimensions.height]);

  // Map percentages to the current pixel size. In realtime mode points only
  // accumulate, so append the new ones; a full setData re-render is needed only
  // when the canvas was recreated at a new size or the data set was replaced.
  useEffect(() => {
    const instance = heatmapInstance.current;
    if (!instance || !dimensions.width || !dimensions.height || !percentages?.length) {
      return;
    }
    const toPixel = (point: PercentagePoint) => ({
      x: Math.round((point.xPercentage / 100) * dimensions.width),
      y: Math.round((point.yPercentage / 100) * dimensions.height),
      value: POINT_INTENSITY,
    });
    const sameCanvas =
      rendered.current.width === dimensions.width && rendered.current.height === dimensions.height;
    try {
      if (isRealtime && sameCanvas && percentages.length >= rendered.current.count) {
        const fresh = percentages.slice(rendered.current.count);
        if (fresh.length) {
          instance.addData(fresh.map(toPixel));
        }
      } else {
        const visible = isRealtime
          ? percentages
          : percentages.slice(0, Math.floor(percentages.length * (percentShown / 100)));
        instance.setData({ data: visible.map(toPixel), min: 0, max: 1 });
      }
      rendered.current = {
        count: percentages.length,
        width: dimensions.width,
        height: dimensions.height,
      };
    } catch (error) {
      console.error("Failed to render heatmap points:", error);
    }
  }, [percentages, dimensions, percentShown, isRealtime]);

  return <Box ref={heatmapRef} w={"100%"} h={"100%"} />;
};

export default HeatMap;
